import React from 'react';
import {useDispatch} from 'react-redux';
import Modal from 'shared/components/modal';
import Icon from 'shared/components/icon';
import IconButton from '@mui/material/IconButton';
import {flashMessage} from 'shared/actions/notifications';
import styles from './styles.module.scss';

interface DnsConfig {
  domain?: string;
  sesVerificationToken?: string;
  sesDkimTokens?: string[];
}

interface Props {
  show: boolean;
  onDismiss: () => void;
  dnsConfig: DnsConfig | null;
}

const DnsSettingsModal = ({show, onDismiss, dnsConfig}: Props) => {
  const dispatch = useDispatch();

  if (!dnsConfig) return null;

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    dispatch(flashMessage('Copied to clipboard!', 'success', {}, 2000));
  };

  const copyIcon = (value: string) => (
    <IconButton onClick={() => copyToClipboard(value)} className={styles.copyButton}>
      <Icon iconType="copy-r" className={styles.copyIcon} />
    </IconButton>
  );

  return (
    <Modal show={show} onClose={onDismiss}>
      <Modal.TextHeader onClose={onDismiss}>
        DNS Configuration for {dnsConfig.domain}
      </Modal.TextHeader>
      <Modal.Body>
        {!dnsConfig.sesVerificationToken && !dnsConfig.sesDkimTokens?.length ? (
          <p>No DNS configuration available. Toggle &quot;DNS&quot; to generate records.</p>
        ) : (
          <>
            <p className="mb-20">
              Add these DNS records to your domain&apos;s DNS settings to verify ownership and
              enable authenticated email sending.
            </p>

            {dnsConfig.sesVerificationToken && (
              <div className="mb-28">
                <h4>1. Domain Verification (TXT Record)</h4>
                <p className="shared-font accent mb-8">
                  Add this TXT record to verify you own the domain:
                </p>
                <div className={styles.recordBox}>
                  <div className={styles.recordRow}>
                    <p>
                      <strong>Type:</strong> TXT
                    </p>
                  </div>
                  <div className={styles.recordRow}>
                    <p>
                      <strong>Name:</strong> <code>_amazonses.{dnsConfig.domain}</code>
                    </p>
                    {copyIcon(`_amazonses.${dnsConfig.domain}`)}
                  </div>
                  <div className={styles.recordRow}>
                    <p>
                      <strong>Value:</strong> <code>{dnsConfig.sesVerificationToken}</code>
                    </p>
                    {copyIcon(dnsConfig.sesVerificationToken)}
                  </div>
                </div>
              </div>
            )}

            {dnsConfig.sesDkimTokens && dnsConfig.sesDkimTokens.length > 0 && (
              <div>
                <h4>2. DKIM Authentication (CNAME Records)</h4>
                <p className={`shared-font accent ${styles.description}`}>
                  Add these 3 CNAME records to authenticate emails:
                </p>
                {dnsConfig.sesDkimTokens.map((token, i) => (
                  <div key={i} className={styles.recordBox}>
                    <div className={styles.recordRow}>
                      <p>
                        <strong>Type:</strong> CNAME
                      </p>
                    </div>
                    <div className={styles.recordRow}>
                      <p>
                        <strong>Name:</strong>{' '}
                        <code>
                          {token}._domainkey.{dnsConfig.domain}
                        </code>
                      </p>
                      {copyIcon(`${token}._domainkey.${dnsConfig.domain}`)}
                    </div>
                    <div className={styles.recordRow}>
                      <p>
                        <strong>Value:</strong> <code>{token}.dkim.amazonses.com</code>
                      </p>
                      {copyIcon(`${token}.dkim.amazonses.com`)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="shared-font accent mt-20">
              <span className="shared-font bold">Note:</span> DNS changes can take up to 48 hours to
              propagate, but typically complete within a few hours.
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer
        primaryButton={{
          text: 'Close',
          onClick: onDismiss,
        }}
      />
    </Modal>
  );
};

export default DnsSettingsModal;
